import React, { useEffect, useMemo, useState } from "react";
import Seo from "@/components/Seo";
import { api } from "@/lib/api";
import AIScreeningZone from "@/components/AIScreeningZone";
import JobDetailsDialog from "@/components/JobDetailsDialog";
import PublicApplyDialog from "@/components/PublicApplyDialog";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Briefcase, MapPin, Search, Sparkles, ArrowRight, Eye, Send } from "lucide-react";

export default function Careers() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [industry, setIndustry] = useState("all");
  const [selectedJobId, setSelectedJobId] = useState(null);
  const [detailsJob, setDetailsJob] = useState(null);
  const [directApplyJob, setDirectApplyJob] = useState(null);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 10;

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/jobs");
        const active = (data || []).filter((j) => j.is_active !== false);
        setJobs(active);
        if (active.length) setSelectedJobId(active[0].job_id);
      } catch {
        setJobs([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const industries = useMemo(() => {
    const set = new Set(jobs.map((j) => j.industry).filter(Boolean));
    return ["all", ...Array.from(set).sort()];
  }, [jobs]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return jobs.filter((j) => {
      if (industry !== "all" && j.industry !== industry) return false;
      if (!needle) return true;
      const hay = [j.title, j.location, j.industry, j.experience, j.description, ...(j.skills || [])]
        .filter(Boolean).join(" ").toLowerCase();
      return hay.includes(needle);
    });
  }, [jobs, q, industry]);

  // Keep the selected job in the filtered result set
  useEffect(() => {
    if (!filtered.find((j) => j.job_id === selectedJobId) && filtered.length) {
      setSelectedJobId(filtered[0].job_id);
    }
  }, [filtered, selectedJobId]);

  // Reset to page 1 whenever the filter result set changes size / shape.
  useEffect(() => { setPage(1); }, [q, industry]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageJobs = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, currentPage]);
  const rangeStart = filtered.length === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(currentPage * PAGE_SIZE, filtered.length);

  const scrollToAIZone = () => {
    // Focus the AI Screening Zone so the resume-upload input is right in view
    // (matters mainly on mobile/tablet where the zone stacks below the list).
    const el = document.querySelector("[data-testid=ai-screening-zone]");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const selectedJob = useMemo(
    () => jobs.find((j) => j.job_id === selectedJobId) || null,
    [jobs, selectedJobId]
  );

  return (
    <div className="pb-24" data-testid="careers-page">
      <Seo
        path="/careers"
        exactTitle="Careers at Ravemora Global | Join Our Team"
        image="https://ravemora.com/og/og-careers.jpg"
        description="Explore career opportunities at Ravemora Global and discover ways to build your career while contributing to our growing team and services."
      />

      {/* HERO */}
      <section className="relative overflow-hidden bg-navy text-offwhite grain">
        <div className="absolute inset-0 opacity-[0.06] pointer-events-none">
          <div className="absolute -top-24 -right-24 w-[520px] h-[520px] rounded-full bg-gold blur-3xl" />
        </div>
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 md:py-24 relative z-10">
          <div className="inline-flex items-center gap-2 label-eyebrow border border-gold/40 px-3 py-1.5">
            <Briefcase size={12} /> Open Positions
          </div>
          <h1 className="serif-display text-4xl md:text-6xl lg:text-[4.5rem] leading-[1.02] mt-8 max-w-4xl">
            Your Future Starts Here: <span className="text-gold">Apply in Seconds.</span>
          </h1>
          <p className="mt-6 text-lg md:text-2xl text-offwhite/85 font-medium max-w-3xl leading-snug">
            Your Next Career Move is Just an Upload Away.
          </p>
        </div>
      </section>

      {/* FILTERS */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 mt-10" data-testid="careers-filters">
        <div className="flex flex-col md:flex-row gap-4 md:items-center md:justify-between">
          <div className="relative flex-1 max-w-xl">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-navy/40" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by title, skill or location…"
              className="pl-9"
              data-testid="careers-search"
            />
          </div>
          <Select value={industry} onValueChange={setIndustry}>
            <SelectTrigger className="w-full md:w-64" data-testid="careers-industry">
              <SelectValue placeholder="Industry" />
            </SelectTrigger>
            <SelectContent>
              {industries.map((i) => (
                <SelectItem key={i} value={i}>{i === "all" ? "All industries" : i}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="mt-3 text-xs uppercase tracking-[0.24em] text-navy/50" data-testid="careers-count">
          {loading ? "Loading…" : `${filtered.length} role${filtered.length === 1 ? "" : "s"} live`}
        </div>
      </section>

      {/* SPLIT LAYOUT — jobs (left) + AI Screening Zone (right, fixed width) */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 mt-8" data-testid="careers-split">
        <div className="grid lg:grid-cols-[minmax(0,1fr)_420px] gap-8">
          {/* LEFT — one card per line */}
          <div className="space-y-3 min-w-0" data-testid="careers-list">
            {loading && Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="border border-navy/10 bg-white p-5 h-32 animate-pulse" />
            ))}
            {!loading && filtered.length === 0 && (
              <div className="border border-dashed border-navy/20 bg-white p-14 text-center text-navy/60">
                No roles match your search. Reset filters or check back next week — new openings are added regularly.
              </div>
            )}
            {pageJobs.map((j) => {
              const selected = j.job_id === selectedJobId;
              return (
                <div
                  key={j.job_id}
                  onClick={() => setSelectedJobId(j.job_id)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && setSelectedJobId(j.job_id)}
                  className={`bg-white p-5 border transition-colors cursor-pointer ${
                    selected ? "border-gold shadow-[0_6px_20px_-12px_rgba(245,183,0,0.55)]" : "border-navy/10 hover:border-navy/30"
                  }`}
                  data-testid={`careers-job-${j.job_id}`}
                >
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.24em] text-gold">
                        <Briefcase size={11} /> {j.industry || "Multi-industry"}
                        {selected && <span className="ml-1 text-navy/50">· Selected</span>}
                      </div>
                      <h3 className="serif-display text-lg md:text-xl text-navy mt-2 leading-tight" data-testid={`careers-job-title-${j.job_id}`}>
                        {j.title}
                      </h3>
                      <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-navy/60">
                        <span className="inline-flex items-center gap-1"><MapPin size={11} /> {j.location || "India"}</span>
                        <span>· {j.experience || "Any exp."}</span>
                        {j.skills?.length ? (
                          <span className="hidden md:inline text-navy/40 truncate">· {j.skills.slice(0, 4).join(" · ")}</span>
                        ) : null}
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 shrink-0" onClick={(e) => e.stopPropagation()}>
                      <button
                        type="button"
                        onClick={() => setDetailsJob(j)}
                        className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-[0.08em] text-navy hover:text-gold px-3 py-2 border border-navy/20 hover:border-gold transition-colors"
                        data-testid={`careers-view-details-${j.job_id}`}
                      >
                        <Eye size={12} /> View Details
                      </button>
                      <button
                        type="button"
                        onClick={() => setDirectApplyJob(j)}
                        className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-[0.08em] text-white bg-navy hover:bg-gold hover:text-navy px-3 py-2 border border-navy hover:border-gold transition-colors"
                        data-testid={`careers-quick-apply-${j.job_id}`}
                      >
                        <Send size={12} /> Apply
                      </button>
                      <button
                        type="button"
                        onClick={() => { setSelectedJobId(j.job_id); scrollToAIZone(); }}
                        className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-[0.08em] text-gold px-1 py-2"
                        data-testid={`careers-screen-${j.job_id}`}
                      >
                        <Sparkles size={12} /> Screen with AI <ArrowRight size={12} />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Pagination */}
            {!loading && filtered.length > 0 && totalPages > 1 && (
              <div className="pt-4 flex flex-col sm:flex-row items-center justify-between gap-3 border-t border-navy/10" data-testid="careers-pagination">
                <div className="text-[11px] uppercase tracking-[0.22em] text-navy/50">
                  Showing <span className="text-navy font-semibold">{rangeStart}–{rangeEnd}</span> of {filtered.length}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => { setPage((p) => Math.max(1, p - 1)); document.querySelector("[data-testid=careers-list]")?.scrollIntoView({ behavior: "smooth", block: "start" }); }}
                    disabled={currentPage <= 1}
                    className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-[0.08em] text-navy hover:text-gold px-3 py-2 border border-navy/20 hover:border-gold transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:text-navy disabled:hover:border-navy/20"
                    data-testid="careers-prev"
                  >
                    ← Previous
                  </button>
                  <span className="text-[11px] uppercase tracking-[0.14em] text-navy/60 px-2" data-testid="careers-page-indicator">
                    Page <span className="text-navy font-semibold">{currentPage}</span> / {totalPages}
                  </span>
                  <button
                    type="button"
                    onClick={() => { setPage((p) => Math.min(totalPages, p + 1)); document.querySelector("[data-testid=careers-list]")?.scrollIntoView({ behavior: "smooth", block: "start" }); }}
                    disabled={currentPage >= totalPages}
                    className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-[0.08em] text-white bg-navy hover:bg-gold hover:text-navy px-3 py-2 border border-navy hover:border-gold transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-navy disabled:hover:text-white disabled:hover:border-navy"
                    data-testid="careers-next"
                  >
                    Next →
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* RIGHT — AI Screening Zone (fixed width so it never expands) */}
          <AIScreeningZone selectedJob={selectedJob} />
        </div>
      </section>

      {/* Details popup */}
      <JobDetailsDialog
        job={detailsJob}
        open={!!detailsJob}
        onOpenChange={(o) => !o && setDetailsJob(null)}
        onApplyDirect={(j) => { setDetailsJob(null); setDirectApplyJob(j); }}
        onScreenWithAI={(j) => { setDetailsJob(null); setSelectedJobId(j.job_id); setTimeout(scrollToAIZone, 100); }}
      />

      {/* Direct-apply modal (no AI screening) */}
      <PublicApplyDialog
        job={directApplyJob}
        open={!!directApplyJob}
        onOpenChange={(o) => !o && setDirectApplyJob(null)}
      />
    </div>
  );
}
